<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleClick"
      >
        Pay
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Confirm your payment
  </DialogHeader>
  <DialogContent>
    <div
      class="w-full flex items-center justify-between pb-[15px] border-b border-gray-800/10 my-[15px]"
    >
      <div>Invoice #{{ invoice?.id }}</div>
      <div class="text-link" @click="openInvoice">View</div>
    </div>
    <div
      class="w-full flex items-center justify-between pb-[15px] border-b border-gray-800/10 mb-[15px]"
    >
      <div class="opacity-50">Total</div>
      <div class="font-medium">{{ amount }}</div>
    </div>
    <div class="w-full flex justify-between mb-5">
      <div class="opacity-50">Payment method</div>
      <div class="text-right">
        <div v-if="billing?.paymentMethod?.card" class="flex items-center">
          <CreditCard
            :type="billing?.paymentMethod?.card?.type"
            class="mr-2.5"
          />
          <div>.... .... .... {{ billing?.paymentMethod?.card?.last4 }}</div>
        </div>
        <div class="text-link" @click="handleChangeClick">
          {{ addOrChange }}
        </div>
      </div>
    </div>

    <Message v-if="error" type="error" class="mb-3">
      Could not pay balance: {{ error }}
    </Message>

    <Message class="text-[12px]!" type="default">
      By selecting "Agree and pay" you are agreeing to our
      <span>
        <a class="text-link -mr-1" href="/legal/terms" target="_blank">
          Terms
        </a>
        ,
      </span>
      <a class="text-link" href="/legal/privacy" target="_blank">
        Privacy Policy
      </a>
      and
      <span>
        <a class="text-link -mr-1" href="/legal/pricing" target="_blank">
          Pricing Terms
        </a>
        .
      </span>
    </Message>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-full"
      primary
      large
      :disabled="!billing?.paymentMethod"
      :loading="loading"
      @click="handleClick"
    >
      Agree and pay
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, Message, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useBillingStore } from '@/features/billing/store'
import { computed, ref } from 'vue'
import CreditCard from '@/features/billing/components/CreditCard.vue'
import {
  BillingQueryDocument,
  InvoiceStatus,
  PlanStatus,
  useInvoicePdfQuery,
  usePayMutation,
  UserPermission,
  type BillingQueryQuery,
} from '@/shared/graphql/legacy/generated'
import { formatPrice } from '@/shared/utils/money'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useWebviewStore } from '@/shared/stores/webview'
import { getStripe } from '@/shared/services/stripe'
import { apolloClient } from '@/shared/graphql/client'
import { useUserStore } from '@/shared/stores/user'
import gql from 'graphql-tag'

const { close, triggerEvent } = useDialog()
const { open } = useDialog()

const { mutate: pay } = usePayMutation()

const billingStore = useBillingStore()
const toastStore = useToastStore()
const webviewStore = useWebviewStore()
const userStore = useUserStore()

const loading = ref(false)
const error = ref('')

const billing = computed(() => billingStore.billing)
const invoice = computed(() =>
  billing.value?.invoices.invoices.find(
    (invoice) => invoice.status === InvoiceStatus.Overdue
  )
)
const amount = computed(() => {
  return formatPrice({
    amount: (invoice.value?.total || 0) / 100,
    currency: invoice.value?.currency,
  })
})

const enabled = computed(() => !!invoice.value?.id)

const { result } = useInvoicePdfQuery(
  () => ({
    invoiceId: invoice.value?.id || '',
  }),
  { enabled }
)

const addOrChange = computed(() =>
  billing.value?.paymentMethod ? 'Change' : 'Add'
)

const openInvoice = () => {
  if (!result.value?.invoicePdf.url) return
  window.open(result.value?.invoicePdf.url, '_blank')
}

const handleChangeClick = () => {
  open(Dialogs.UpdatePaymentMethod, { stack: true })
  error.value = ''
}

const handleRequiresAction = async (clientSecret: string) => {
  if (webviewStore.isActive) {
    return
  }

  const stripe = await getStripe()
  if (!stripe) return

  const { error: stripeError } = await stripe.confirmCardPayment(clientSecret)

  if (stripeError) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Payment authentication failed',
      error: new Error(stripeError.message),
    })
    return
  }

  if (!userStore.user) return

  apolloClient.cache.updateFragment(
    {
      id: apolloClient.cache.identify(userStore.user),
      fragment: gql`
        fragment UpdateUser on User {
          permissions
        }
      `,
    },
    (response) => {
      if (!response) return
      return {
        permissions: userStore.user?.permissions.concat(UserPermission.Pro),
      }
    }
  )

  apolloClient.cache.updateQuery<BillingQueryQuery>(
    { query: BillingQueryDocument },
    (response) => {
      if (!response) return
      return {
        ...response,
        billing: {
          ...response.billing,
          plan: {
            ...response.billing.plan,
            status: PlanStatus.Active,
            dateInfo: {
              __typename: 'BillingPlanDateInfo',
              date: response.billing.upcomingDetails.upcomingBilling?.date,
              prefix: 'Renews on',
            },
          },
          billingAlert: null,
        },
      }
    }
  )
}

const handleClick = async () => {
  try {
    loading.value = true

    const response = await pay(
      {},
      {
        update(cache) {
          cache.updateQuery<BillingQueryQuery>(
            { query: BillingQueryDocument },
            (response) => {
              if (!response) return
              return {
                ...response,
                billing: {
                  ...response.billing,
                  billingAlert: null,
                },
              }
            }
          )
        },
      }
    )

    if (response?.data?.pay.paymentMethod?.requiresAction) {
      await handleRequiresAction(
        response.data.pay.paymentMethod.requiresAction.clientSecret
      )
    }

    triggerEvent('success')
  } catch (err) {
    loading.value = false
    error.value = (err as Error).message
  }
}
</script>
